// @todo: Migrate to 'Toast' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {toast} from '@wandb/weave/common/components/elements/Toast';
import React, {createContext, useContext, useMemo, useState} from 'react';

import {useDispatch} from '../../../../state/hooks';
import {usePanelBankSectionsPagingContext} from '../../../../state/panelbank/PanelBankSectionsPagingContext';
import {usePart, usePartMaybe} from '../../../../state/views/hooks';
import * as PanelBankConfigActions from '../../../../state/views/panelBankConfig/actions';
import * as PanelBankSectionConfigActions from '../../../../state/views/panelBankSectionConfig/actions';
import {LayedOutPanel} from '../../../../util/panelTypes';
import {AddVisNewPanelEditor} from '../../../AddVisNewPanelEditor';
import {SectionPanelSorting} from '../../../PanelBank/types';
import {useWorkspaceRefsContext} from '../../../Workspace/WorkspaceRefsContext';
import {useWorkspaceInheritedSettingsForPanel} from '../../Settings/hooks/useWorkspaceInheritedSettingsForPanel';
import {useSectionSettingsContextMaybe} from '../../Settings/SectionSettingsContextProvider';
import {useWorkspaceSettingsContext} from '../../Settings/WorkspaceSettingsContextProvider';
import {useWorkspaceDrawerContext} from '../../WorkspaceDrawerContextProvider';

type NewPanelEditorContextValue = {
  setNewPanel: (panel: LayedOutPanel) => void;
};
type NewPanelEditorContextProviderProps = {
  children: React.ReactNode;
  groupTitle?: string;
};

const NewPanelEditorContext = createContext<
  NewPanelEditorContextValue | undefined
>(undefined);

export const NewPanelEditorContextProvider = ({
  children,
  groupTitle,
}: NewPanelEditorContextProviderProps) => {
  const inheritedSettings = useWorkspaceInheritedSettingsForPanel();
  const dispatch = useDispatch();
  const {setCurrentPageForSectionRefID} = usePanelBankSectionsPagingContext();
  const {closeWorkspaceDrawer, targetSectionRef: drawerSectionRef} =
    useWorkspaceDrawerContext();
  const isGlobalAdd = drawerSectionRef == null;

  const {
    workspaceLayout: {sortAlphabetically},
  } = useWorkspaceSettingsContext();

  const {panelBankConfigRef} = useWorkspaceRefsContext();
  const panelBankPart = usePart(panelBankConfigRef);

  // If panel is to be added to an existing section, a SectionSettingsContext
  // will be available, and the sectionRef from that context should be used.
  // This may be different from the targetSectionRef in WorkspaceDrawerContext
  // because an existing/matching section may be available even for global add.
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  const sectionPart = usePartMaybe(sectionRef);

  const [newPanel, setNewPanel] = useState<LayedOutPanel>();
  const value = useMemo(() => ({setNewPanel}), []);

  return (
    <NewPanelEditorContext.Provider value={value}>
      {children}

      {newPanel != null && groupTitle && (
        <AddVisNewPanelEditor
          currentHeight={400}
          inheritedSettings={inheritedSettings}
          initialEditingState
          panel={newPanel}
          onCancel={() => {
            setNewPanel(undefined);
          }}
          onOK={panelRef => {
            setNewPanel(undefined);
            if (sectionRef) {
              dispatch(
                PanelBankSectionConfigActions.addPanelByRef(
                  sectionRef,
                  panelRef,
                  sortAlphabetically
                )
              );
              if (isGlobalAdd && sectionPart?.name) {
                toast(`Panel added to ${sectionPart.name} section`);
              } else if (
                sectionPart?.sorted === SectionPanelSorting.Alphabetical
              ) {
                toast('Panel added alphabetically to section');
              } else {
                // default sorting adds new panels to the first page
                setCurrentPageForSectionRefID(sectionRef.id, 0);
              }
            } else {
              dispatch(
                PanelBankConfigActions.addSection(
                  panelBankConfigRef,
                  panelBankPart.sectionRefs[0],
                  {
                    newSectionName: groupTitle,
                    panelRefs: [panelRef],
                  }
                )
              );
              toast(`Panel added to ${groupTitle} section`);
            }
            closeWorkspaceDrawer();
          }}
        />
      )}
    </NewPanelEditorContext.Provider>
  );
};

export const useNewPanelEditorContext = (): NewPanelEditorContextValue => {
  const value = useContext(NewPanelEditorContext);
  if (value == null) {
    throw new Error(
      'useNewPanelEditorContext must be used within a NewPanelEditorContextProvider'
    );
  }
  return value;
};
